import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  Chip,
  Alert,
  Avatar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  VpnKey as VpnKeyIcon,
  Google as GoogleIcon,
  Email as EmailIcon,
  PhotoCamera as PhotoCameraIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../services/firebase';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, deleteUser, updateProfile } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const ProfileManageModal = ({
  open,
  onClose,
  user,
  userEmail,
  onSave,
  onPasswordChange,
  onAccountDelete
}) => {
  
  // 모달 닫힐 때 상태 초기화
  const handleClose = () => {
    setImageError('');
    setPasswordError('');
    setPasswordSuccess('');
    setDeleteError('');
    onClose();
  };
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 편집 상태 관리
  const [editingField, setEditingField] = useState(null);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    email: userEmail || ''
  });
  const [tempValue, setTempValue] = useState('');
  const [loginMethods, setLoginMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 비밀번호 변경 관련 상태
  const [isPasswordChangeOpen, setIsPasswordChangeOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // 회원탈퇴 관련 상태
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // 프로필 이미지 관련 상태
  const [profileImage, setProfileImage] = useState('');
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');

  // 사용자 정보 초기화
  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        const displayName = user.displayName || '';
        const nameParts = displayName.split(' ');
        
        setProfileData({
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          nickname: displayName || '',
          email: userEmail || user.email || ''
        });
        
        // 프로필 이미지 초기화
        setProfileImage(user.photoURL || '');
        
        // Firestore에서 실제 로그인 방식 데이터 가져오기
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const loginMethod = userData.loginMethod;
            const methods = [];
            
            if (Array.isArray(loginMethod)) {
              // loginMethod가 배열인 경우
              loginMethod.forEach(method => {
                switch (method) {
                  case 'email':
                    methods.push({ type: 'email', verified: user.emailVerified });
                    break;
                  case 'google':
                    methods.push({ type: 'google', verified: true });
                    break;
                  case 'kakao':
                    methods.push({ type: 'kakao', verified: true });
                    break;
                  case 'naver':
                    methods.push({ type: 'naver', verified: true });
                    break;
                  default:
                    break;
                }
              });
            } else if (typeof loginMethod === 'string') {
              // loginMethod가 문자열인 경우
              switch (loginMethod) {
                case 'email':
                  methods.push({ type: 'email', verified: user.emailVerified });
                  break;
                case 'google':
                  methods.push({ type: 'google', verified: true });
                  break;
                case 'kakao':
                  methods.push({ type: 'kakao', verified: true });
                  break;
                case 'naver':
                  methods.push({ type: 'naver', verified: true });
                  break;
                default:
                  break;
              }
            }
            
            setLoginMethods(methods);
            console.log('로그인 방식 데이터:', methods);
          } else {
            console.warn('Firestore에서 사용자 데이터를 찾을 수 없음');
            // Firestore에 데이터가 없는 경우 Firebase Auth 정보로 폴백
            const methods = [];
            if (user.email) {
              methods.push({ type: 'email', verified: user.emailVerified });
            }
            setLoginMethods(methods);
          }
        } catch (error) {
          console.error('Firestore에서 사용자 데이터 가져오기 실패:', error);
          // 에러 발생 시 Firebase Auth 정보로 폴백
          const methods = [];
          if (user.email) {
            methods.push({ type: 'email', verified: user.emailVerified });
          }
          setLoginMethods(methods);
        }
      }
    };

    fetchUserData();
  }, [user, userEmail]);

  const handleFieldEdit = (field) => {
    setEditingField(field);
    setTempValue(profileData[field]);
  };

  const handleFieldSave = () => {
    setProfileData(prev => ({
      ...prev,
      [editingField]: tempValue
    }));
    setEditingField(null);
    setTempValue('');
  };

  const handleFieldCancel = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Firebase Auth 프로필 업데이트
      if (user && (profileData.firstName || profileData.lastName)) {
        const displayName = `${profileData.firstName} ${profileData.lastName}`.trim();
        await updateProfile(user, {
          displayName: displayName
        });
      }

      // Firestore 사용자 데이터 업데이트
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          displayName: `${profileData.firstName} ${profileData.lastName}`.trim(),
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          nickname: profileData.nickname
        });
      }

      console.log('✅ 프로필 정보 저장 성공');
      handleClose();
    } catch (error) {
      console.error('프로필 저장 실패:', error);
      setError(t('profile.profileSaveError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChangeToggle = () => {
    setIsPasswordChangeOpen(!isPasswordChangeOpen);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordDataChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    setPasswordError('');
  };

  const handlePasswordSubmit = async () => {
    try {
      setPasswordError('');
      setIsLoading(true);
      
      // 🔥 1단계: 클라이언트 측 유효성 검사
      if (!passwordData.currentPassword) {
        setPasswordError(t('profile.currentPasswordRequired'));
        return;
      }
      
      if (!passwordData.newPassword) {
        setPasswordError(t('profile.newPasswordRequired'));
        return;
      }
      
      if (passwordData.newPassword.length < 6) {
        setPasswordError(t('profile.passwordTooShort'));
        return;
      }
      
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError(t('profile.passwordMismatch'));
        return;
      }
      
      if (passwordData.currentPassword === passwordData.newPassword) {
        setPasswordError(t('profile.samePassword'));
        return;
      }
      
      // 🔥 2단계: Firebase를 통한 현재 비밀번호 검증
      if (!user || !user.email) {
        setPasswordError(t('profile.userNotFound'));
        return;
      }
      
      try {
        // 현재 비밀번호로 재인증 시도
        const credential = EmailAuthProvider.credential(user.email, passwordData.currentPassword);
        await reauthenticateWithCredential(user, credential);
        console.log('✅ 현재 비밀번호 검증 성공');
      } catch (reauthError) {
        console.error('❌ 현재 비밀번호 검증 실패:', reauthError);
        if (reauthError.code === 'auth/wrong-password' || reauthError.code === 'auth/invalid-credential') {
          setPasswordError(t('profile.wrongPassword'));
        } else if (reauthError.code === 'auth/too-many-requests') {
          setPasswordError(t('profile.tooManyRequests'));
        } else {
          setPasswordError(t('profile.passwordVerificationFailed'));
        }
        return;
      }
      
      // 🔥 3단계: 새 비밀번호로 업데이트
      try {
        await updatePassword(user, passwordData.newPassword);
        console.log('✅ 비밀번호 변경 성공');
        
        // 성공 메시지 표시
        setPasswordSuccess(t('profile.passwordChangeSuccess'));
        
        // 2초 후 성공 메시지 제거 및 폼 닫기
        setTimeout(() => {
          setPasswordSuccess('');
          setIsPasswordChangeOpen(false);
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        }, 2000);
        
      } catch (updateError) {
        console.error('❌ 비밀번호 업데이트 실패:', updateError);
        if (updateError.code === 'auth/weak-password') {
          setPasswordError(t('profile.weakPassword'));
        } else {
          setPasswordError(t('profile.passwordUpdateFailed'));
        }
        return;
      }
      
    } catch (error) {
      console.error('❌ 예상치 못한 오류:', error);
      setPasswordError(t('profile.unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setIsPasswordChangeOpen(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  // 프로필 이미지 변경 핸들러
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsImageUploading(true);
      setImageError('');

      // 파일 크기 검사 (5MB 제한)
      if (file.size > 5 * 1024 * 1024) {
        setImageError(t('profile.imageSizeError'));
        return;
      }

      // 파일 타입 검사
      if (!file.type.startsWith('image/')) {
        setImageError(t('profile.imageTypeError'));
        return;
      }

      // Firebase Storage에 업로드
      const imageRef = ref(storage, `profile-images/${user.uid}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Firebase Auth 프로필 업데이트
      await updateProfile(user, {
        photoURL: downloadURL
      });

      // Firestore 사용자 데이터 업데이트
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        photoURL: downloadURL
      });

      // 로컬 상태 업데이트
      setProfileImage(downloadURL);

      console.log('✅ 프로필 이미지 업데이트 성공:', downloadURL);
      
      // 성공 메시지 (임시로 간단히 표시)
      setImageError('');
      // 이미지 업로드 성공은 별도의 성공 상태 관리 없이 바로 반영

    } catch (error) {
      console.error('❌ 프로필 이미지 업로드 실패:', error);
      setImageError(t('profile.imageUploadError'));
    } finally {
      setIsImageUploading(false);
    }
  };

  const handleAccountDeleteClick = () => {
    setDeleteConfirmOpen(true);
    setDeletePassword('');
    setDeleteError('');
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeletePassword('');
    setDeleteError('');
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteError('');
      setIsDeleting(true);

      if (!user || !user.email) {
        setDeleteError(t('profile.userNotFound'));
        return;
      }

      // 이메일 로그인 사용자인 경우 비밀번호 확인 필요
      const hasEmailLogin = loginMethods.some(method => method.type === 'email');
      
      if (hasEmailLogin) {
        if (!deletePassword) {
          setDeleteError(t('profile.passwordRequired'));
          return;
        }

        try {
          // 현재 비밀번호로 재인증
          const credential = EmailAuthProvider.credential(user.email, deletePassword);
          await reauthenticateWithCredential(user, credential);
          console.log('✅ 회원탈퇴용 비밀번호 검증 성공');
        } catch (reauthError) {
          console.error('❌ 회원탈퇴용 비밀번호 검증 실패:', reauthError);
          if (reauthError.code === 'auth/wrong-password' || reauthError.code === 'auth/invalid-credential') {
            setDeleteError(t('profile.passwordIncorrect'));
          } else {
            setDeleteError(t('profile.passwordConfirmationFailed'));
          }
          return;
        }
      }

      // Firestore에서 사용자 데이터 삭제
      try {
        await deleteDoc(doc(db, 'users', user.uid));
        console.log('✅ Firestore 사용자 데이터 삭제 성공');
      } catch (firestoreError) {
        console.error('❌ Firestore 데이터 삭제 실패:', firestoreError);
        // Firestore 삭제 실패해도 Auth 삭제는 계속 진행
      }

      // Firebase Auth에서 사용자 삭제
      await deleteUser(user);
      console.log('✅ Firebase Auth 사용자 삭제 성공');

      // 성공 시 모달 닫기 (로그아웃됨)
      onClose();
      
      // 탈퇴 완료 메시지
      alert(t('profile.accountDeleteSuccess'));

    } catch (error) {
      console.error('❌ 회원탈퇴 실패:', error);
      if (error.code === 'auth/requires-recent-login') {
        setDeleteError(t('profile.requiresRecentLogin'));
      } else {
        setDeleteError(t('profile.accountDeleteFailed'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const getLoginMethodIcon = (type) => {
    switch (type) {
      case 'email':
        return <EmailIcon fontSize="small" />;
      case 'google':
        return <GoogleIcon fontSize="small" />;
      case 'kakao':
        return <Box component="span" sx={{ fontSize: '16px', fontWeight: 'bold' }}>K</Box>;
      case 'naver':
        return <Box component="span" sx={{ fontSize: '16px', fontWeight: 'bold', color: '#03C75A' }}>N</Box>;
      default:
        return <EmailIcon fontSize="small" />;
    }
  };

  const getLoginMethodLabel = (type, verified) => {
    let label = '';
    switch (type) {
      case 'email':
        label = t('profile.emailLogin');
        break;
      case 'google':
        label = t('profile.googleLogin');
        break;
      case 'kakao':
        label = t('profile.kakaoLogin');
        break;
      case 'naver':
        label = t('profile.naverLogin');
        break;
      default:
        label = t('profile.emailLogin');
    }
    
    if (type === 'email' && !verified) {
      label += ` (${t('profile.unverified')})`;
    }
    
    return label;
  };

  const renderProfileField = (field, label, value, readonly = false) => {
    const isEditing = editingField === field && !readonly;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" sx={{ minWidth: 80, color: '#666' }}>
          {label}
        </Typography>
        {isEditing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <TextField
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              size="small"
              fullWidth
              autoFocus
            />
            <Button size="small" onClick={handleFieldSave} variant="contained">
              {t('profile.save')}
            </Button>
            <Button size="small" onClick={handleFieldCancel}>
              {t('profile.cancel')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {value || t('profile.notSet')}
            </Typography>
            {!readonly && (
              <IconButton size="small" onClick={() => handleFieldEdit(field)}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {readonly && (
              <Typography variant="caption" sx={{ color: '#999', fontSize: '12px' }}>
                {t('profile.readOnly')}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100%' : '80vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('profile.title')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 기본 정보 섹션 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('profile.basicInfo')}
          </Typography>
          
          {/* 프로필 이미지 섹션 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, justifyContent: 'center' }}>
            <Avatar
              src={profileImage}
              sx={{
                width: 100,
                height: 100,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.02)'
                },
                border: '3px solid #e0e0e0',
                transition: 'all 0.2s ease'
              }}
              onClick={() => document.getElementById('profile-image-input').click()}
            >
              {!profileImage && <PersonIcon sx={{ fontSize: 50 }} />}
            </Avatar>
            
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 500 }}>
                {t('profile.profileImage')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => document.getElementById('profile-image-input').click()}
                disabled={isImageUploading}
                sx={{ 
                  fontSize: '12px',
                  minWidth: 'auto',
                  px: 2
                }}
              >
                {isImageUploading ? t('profile.uploading') : t('profile.change')}
              </Button>
              {imageError && (
                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
                  {imageError}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5, fontSize: '11px' }}>
                {t('profile.clickToChange')}
              </Typography>
            </Box>
            
            {/* 숨겨진 파일 입력 */}
            <input
              id="profile-image-input"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
          </Box>
          
          {renderProfileField('firstName', t('profile.firstName'), profileData.firstName)}
          {renderProfileField('lastName', t('profile.lastName'), profileData.lastName)}
          {renderProfileField('nickname', t('profile.nickname'), profileData.nickname)}
          {renderProfileField('email', t('profile.email'), profileData.email, true)}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* 로그인 방식 섹션 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('profile.loginMethods')}
          </Typography>
          
          {loginMethods.map((method, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: isPasswordChangeOpen && method.type === 'email' ? '8px 8px 0 0' : 2,
                  borderBottom: isPasswordChangeOpen && method.type === 'email' ? 'none' : '1px solid #e0e0e0'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getLoginMethodIcon(method.type)}
                  <Typography variant="body2">
                    {getLoginMethodLabel(method.type, method.verified)}
                  </Typography>
                </Box>
                
                {method.type === 'email' && (
                  <Button
                    size="small"
                    variant={isPasswordChangeOpen ? "contained" : "outlined"}
                    startIcon={<VpnKeyIcon />}
                    onClick={handlePasswordChangeToggle}
                    sx={{ fontSize: '12px' }}
                  >
                    {isPasswordChangeOpen ? t('profile.close') : t('profile.passwordChange')}
                  </Button>
                )}
              </Box>
              
              {/* 비밀번호 변경 폼 (확장) */}
              {method.type === 'email' && isPasswordChangeOpen && (
                <Box
                  sx={{
                    p: 3,
                    border: '1px solid #e0e0e0',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    bgcolor: '#f9f9f9'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('profile.passwordChange')}
                  </Typography>
                  
                  {passwordError && (
                    <Alert severity="error" sx={{ mb: 2, fontSize: '14px' }}>
                      {passwordError}
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label={t('profile.currentPassword')}
                      type="password"
                      size="small"
                      fullWidth
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordDataChange('currentPassword', e.target.value)}
                      autoComplete="current-password"
                    />
                    <TextField
                      label={t('profile.newPassword')}
                      type="password"
                      size="small"
                      fullWidth
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordDataChange('newPassword', e.target.value)}
                      autoComplete="new-password"
                      error={
                        (passwordData.newPassword && passwordData.newPassword.length > 0 && passwordData.newPassword.length < 6) ||
                        (passwordData.newPassword && passwordData.currentPassword && passwordData.newPassword === passwordData.currentPassword)
                      }
                      helperText={
                        passwordData.newPassword && passwordData.currentPassword && passwordData.newPassword === passwordData.currentPassword
                          ? t('profile.currentPasswordSame')
                          : passwordData.newPassword && passwordData.newPassword.length > 0 && passwordData.newPassword.length < 6
                          ? t('profile.passwordMinLength')
                          : t('profile.passwordMinLength')
                      }
                    />
                    <TextField
                      label={t('profile.confirmNewPassword')}
                      type="password"
                      size="small"
                      fullWidth
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordDataChange('confirmPassword', e.target.value)}
                      autoComplete="new-password"
                      error={passwordData.confirmPassword && passwordData.newPassword && passwordData.newPassword !== passwordData.confirmPassword}
                      helperText={
                        passwordData.confirmPassword && passwordData.newPassword && passwordData.newPassword !== passwordData.confirmPassword
                          ? t('profile.passwordsMismatch')
                          : ""
                      }
                    />
                    
                    {/* 성공 메시지 */}
                    {passwordSuccess && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, textAlign: 'center' }}>
                          ✅ {passwordSuccess}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        size="small"
                        onClick={handlePasswordCancel}
                        color="inherit"
                      >
                        {t('profile.cancel')}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handlePasswordSubmit}
                        disabled={
                          isLoading ||
                          passwordSuccess ||
                          !passwordData.currentPassword || 
                          !passwordData.newPassword || 
                          !passwordData.confirmPassword ||
                          passwordData.newPassword !== passwordData.confirmPassword ||
                          passwordData.newPassword.length < 6 ||
                          passwordData.currentPassword === passwordData.newPassword
                        }
                      >
                        {passwordSuccess ? t('profile.changeComplete') : isLoading ? t('profile.changing') : t('profile.confirm')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleAccountDeleteClick}
            sx={{ mr: 'auto' }}
          >
            {t('profile.deleteAccount')}
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleClose} color="inherit">
              {t('profile.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? t('profile.saving') : t('profile.save')}
            </Button>
          </Box>
        </Box>
      </DialogActions>

      {/* 회원탈퇴 확인 다이얼로그 */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f' }}>
          ⚠️ {t('profile.deleteAccountConfirm')}
        </DialogTitle>
        
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            {t('profile.deleteAccountTitle')}
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {t('profile.deleteAccountWarning')}
            </Typography>
          </Alert>

          {loginMethods.some(method => method.type === 'email') && (
            <TextField
              label={t('profile.passwordConfirmation')}
              type="password"
              fullWidth
              size="small"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              error={!!deleteError}
              helperText={deleteError || t('profile.passwordConfirmationHelp')}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDeleteCancel} 
            color="inherit"
            disabled={isDeleting}
          >
            {t('profile.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={
              isDeleting || 
              (loginMethods.some(method => method.type === 'email') && !deletePassword)
            }
          >
            {isDeleting ? t('profile.deleteInProgress') : t('profile.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ProfileManageModal; 